<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { ACCOUNT_TIMEZONE_OPTIONS } from 'dashboard/constants/timezones';
import SectionLayout from './SectionLayout.vue';

defineProps({
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const accountTimezone = ref('');
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { account_timezone } = currentAccount.value?.settings || {};
    accountTimezone.value = account_timezone || '';
  },
  { deep: true, immediate: true }
);

const saveTimezone = async () => {
  isSubmitting.value = true;
  try {
    await updateAccount({
      account_timezone: accountTimezone.value || null,
    });
    useAlert(t('GENERAL_SETTINGS.FORM.ACCOUNT_TIMEZONE.API.SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.ACCOUNT_TIMEZONE.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.ACCOUNT_TIMEZONE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.ACCOUNT_TIMEZONE.NOTE')"
    with-border
  >
    <form
      class="grid w-full gap-4"
      :class="{ 'max-w-md': !fullWidth }"
      @submit.prevent="saveTimezone"
    >
      <WithLabel
        name="account-timezone"
        :label="t('GENERAL_SETTINGS.FORM.ACCOUNT_TIMEZONE.LABEL')"
      >
        <select v-model="accountTimezone" class="!mb-0 w-full text-sm">
          <option value="">
            {{ t('GENERAL_SETTINGS.FORM.ACCOUNT_TIMEZONE.PLACEHOLDER') }}
          </option>
          <option
            v-for="option in ACCOUNT_TIMEZONE_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </WithLabel>
      <div>
        <NextButton blue :is-loading="isSubmitting" type="submit">
          {{ t('GENERAL_SETTINGS.SUBMIT') }}
        </NextButton>
      </div>
    </form>
  </SectionLayout>
</template>
